import React, { useState, useEffect } from 'react';
import { referencesAPI } from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import './References.css';

function References() {
  const [htsCodes, setHtsCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHTSCodes();
  }, []);

  const fetchHTSCodes = async () => {
    try {
      const response = await referencesAPI.getHTSCodes();
      setHtsCodes(response.data.hts_codes || []);
    } catch (err) {
      console.error('Failed to fetch HTS codes:', err);
    }
  };

  const handleDownload = async (filename, displayName) => {
    setLoading(true);
    try {
      const response = await referencesAPI.downloadDocument(filename);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
      alert('Failed to download document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="references" />

      <div className="content">
        <h1 className="page-title">Reference Documents</h1>

        <div className="references-container">
          <section className="reference-section">
            <h2>Reference Documents</h2>
            <div className="reference-card">
              <div className="document-list">
                <div className="document-item">
                  <span>Domestic Entry Instructions</span>
                  <button
                    className="btn-download"
                    onClick={() => handleDownload('domestic-entry', 'DomesticEntry4.pdf')}
                    disabled={loading}
                  >
                    download
                  </button>
                </div>
                <div className="document-item">
                  <span>International Entry Instructions</span>
                  <button
                    className="btn-download"
                    onClick={() => handleDownload('international-entry', 'InternationalEntry4.pdf')}
                    disabled={loading}
                  >
                    download
                  </button>
                </div>
                <div className="document-item">
                  <span>Quick Tip Guide</span>
                  <button
                    className="btn-download"
                    onClick={() => handleDownload('quick-tips', 'quicktips.pdf')}
                    disabled={loading}
                  >
                    download
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="reference-section">
            <h2>Frequently Used HTS Codes</h2>
            <div className="reference-card">
              {htsCodes.length === 0 ? (
                <p>No HTS codes available</p>
              ) : (
                <table className="hts-table">
                  <thead>
                    <tr>
                      <th>Part Num</th>
                      <th>Description</th>
                      <th>HTS Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {htsCodes.map((code, idx) => (
                      <tr key={idx}>
                        <td>{code.part_num}</td>
                        <td>{code.description}</td>
                        <td>{code.hts_code}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="reference-section">
            <h2>Contact Information</h2>
            <div className="reference-card">
              <h3>Shipping Portal Support</h3>
              <p>
                <strong>General Shipping:</strong>{' '}
                <a href="mailto:psmshipment@us.psm.com">psmshipment@us.psm.com</a>
              </p>
            </div>
          </section>

          <section className="reference-section">
            <h2>Important Notes</h2>
            <div className="reference-card">
              <h3>Domestic Shipments</h3>
              <p>
                All vendor domestic shipping requests will be subject to a 24 hour review to verify
                potential savings.
              </p>
            </div>
            <div className="reference-card">
              <h3>International Shipments</h3>
              <p>
                All international shipments will be booked within 2 business days. Questions on
                Shipping Portal requests should be directed to Mike Lyons.
              </p>
            </div>
          </section>

          <section className="reference-section">
            <h2>Service Types</h2>
            <div className="reference-card">
              <h3>Domestic Services</h3>
              <ul>
                <li>
                  <strong>Ground:</strong> Standard ground shipping
                </li>
                <li>
                  <strong>2-Day:</strong> Two business day delivery
                </li>
                <li>
                  <strong>Next Day:</strong> Next business day delivery
                </li>
                <li>
                  <strong>LTL:</strong> Less than truckload freight
                </li>
                <li>
                  <strong>FTL:</strong> Full truckload
                </li>
              </ul>
            </div>
            <div className="reference-card">
              <h3>International Services</h3>
              <ul>
                <li>
                  <strong>Air Freight:</strong> Fast international air shipping
                </li>
                <li>
                  <strong>Ocean Freight:</strong> Cost-effective sea shipping
                </li>
                <li>
                  <strong>Express:</strong> Expedited international delivery
                </li>
              </ul>
            </div>
          </section>

          <section className="reference-section">
            <h2>Accessorial Services</h2>
            <div className="reference-card">
              <ul>
                <li>
                  <strong>Liftgate Service:</strong> For locations without loading dock
                </li>
                <li>
                  <strong>Inside Delivery:</strong> Delivery inside the building
                </li>
                <li>
                  <strong>Rigging:</strong> Special equipment handling
                </li>
                <li>
                  <strong>Construction Site:</strong> Delivery to construction locations
                </li>
                <li>
                  <strong>Tarp Service:</strong> Weather protection during transport
                </li>
                <li>
                  <strong>Freeze Protection:</strong> Temperature-controlled shipping
                </li>
              </ul>
            </div>
          </section>

          <section className="reference-section">
            <h2>Billing Options</h2>
            <div className="reference-card">
              <ul>
                <li>
                  <strong>Prepaid:</strong> Shipper pays freight charges
                </li>
                <li>
                  <strong>Collect:</strong> Consignee pays freight charges
                </li>
                <li>
                  <strong>Third Party:</strong> Another party pays freight charges
                </li>
              </ul>
            </div>
          </section>

          <section className="reference-section">
            <h2>Useful Links</h2>
            <div className="reference-card">
              <ul>
                <li>
                  <a href="https://www.fedex.com" target="_blank" rel="noopener noreferrer">
                    FedEx Tracking
                  </a>
                </li>
                <li>
                  <a href="https://www.ups.com" target="_blank" rel="noopener noreferrer">
                    UPS Tracking
                  </a>
                </li>
                <li>
                  <a href="https://www.dhl.com" target="_blank" rel="noopener noreferrer">
                    DHL Tracking
                  </a>
                </li>
                <li>
                  <a href="https://www.freightquote.com" target="_blank" rel="noopener noreferrer">
                    Freight Quote
                  </a>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default References;
